import { useState, type ReactNode } from "react";
import { Badge, Button, Chip, Popover, Typography } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: theme.spacing(1),
    },
    badge: {
        "& .MuiBadge-badge": { right: -2, top: 2 },
    },
    chipRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    panel: {
        padding: theme.spacing(2),
        width: 340,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    title: { fontWeight: 700, color: theme.palette.text.primary },
    fields: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
}));

export interface FilterChip {
    key: string;
    label: string;
    onRemove: () => void;
}

interface TableFiltersProps {
    /** The filter controls, stacked in the popover. */
    children: ReactNode;
    /** Shown as "Filter {label}" at the top of the popover. */
    label: string;
    /** One per applied filter. Drives the badge count and the summary chips. */
    chips?: FilterChip[];
    onClearAll: () => void;
    /** Run when Done is pressed, for filters that need an explicit apply (e.g. search). */
    onApply?: () => void;
    disabled?: boolean;
}

export default function TableFilters({ children, label, chips = [], onClearAll, onApply, disabled = false }: TableFiltersProps) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const done = () => {
        onApply?.();
        setAnchorEl(null);
    };

    return (
        <div className={classes.root}>
            <Badge className={classes.badge} badgeContent={chips.length} color="secondary">
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={disabled}
                    startIcon={<FilterList />}
                    aria-label={`Filter ${label}`}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                    Filters
                </Button>
            </Badge>

            {chips.length > 0 && (
                <div className={classes.chipRow}>
                    {chips.map((chip) => (
                        <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onRemove} />
                    ))}
                    <Button color="primary" size="small" onClick={onClearAll}>
                        Clear all
                    </Button>
                </div>
            )}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <div className={classes.panel}>
                    <Typography className={classes.title}>Filter {label}</Typography>
                    <div className={classes.fields}>{children}</div>
                    <div className={classes.footer}>
                        <Button color="primary" size="small" disabled={chips.length === 0} onClick={onClearAll}>
                            Clear all
                        </Button>
                        <Button variant="contained" color="primary" size="small" onClick={done}>
                            Done
                        </Button>
                    </div>
                </div>
            </Popover>
        </div>
    );
}
